import { useQuery } from "@tanstack/react-query";

import { get, getAuthorizationHeader } from "@/api";
import {
  getGiftCardsTransfersByOrderByOrderId,
  getOrdersCurrentSummary,
  getShopDiscounts,
  getShopDiscountsListingsByListingId,
} from "@/api/methods";
import { queryClient } from "@/pages/Root";

import { queryKeyFor } from "api/queryKeys";
import type { GetPaths } from "api/types";
export const CURRENT_ORDER_QUERY_KEY = queryKeyFor(
  "/orders/current/details" satisfies GetPaths,
  undefined,
);

export function useCurrentOrderDetails(options = {}) {
  return useQuery({
    queryKey: CURRENT_ORDER_QUERY_KEY,
    // No active cart is a 204 (GetCartDetailsCommand -> NoContentException),
    // which `get()` surfaces as null; real failures reject the query.
    queryFn: () => get("/orders/current/details"),
    // The endpoint is [Authorize]: signed out there is no cart to fetch.
    enabled: getAuthorizationHeader() !== undefined,

    staleTime: 10 * 1000, // 10 seconds
    cacheTime: 10 * 60 * 1000,

    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 1,
    ...options,
  });
}

export function invalidateCurrentOrderDetails() {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: getOrdersCurrentSummary.key({ includePackageLineItems: false }),
    }),
    queryClient.invalidateQueries({
      queryKey: CURRENT_ORDER_QUERY_KEY,
    }),
    // The cart renders a gift-card line's recipient by looking its packageTransferId up in this
    // list, and falls back to "needs recipient" when the lookup misses. Anything that changes the
    // cart can change its gift-card lines — applying a buy X get Y offer seeds a reward card with a
    // transfer already assigned to the buyer — so a list fetched before the change would report a
    // card that does have a recipient as missing one. staleTime alone does not save it: the cart
    // stays mounted and refetchOnWindowFocus is off, so nothing re-runs the query until this.
    queryClient.invalidateQueries(getGiftCardsTransfersByOrderByOrderId.filters()),
  ]);
}

/**
 * The shop advertises offers from these two reads, which the PDP loader and the hover prefetch
 * keep for the catalog stale time. A paid order can consume a single-use offer and a location
 * switch changes which offers apply, so both have to drop them, or a listing keeps advertising an
 * offer the patient can no longer use. The per-listing read is keyed by listing id, hence the
 * path-prefix match.
 */
export function invalidateShopDiscounts() {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: getShopDiscounts.key() }),
    queryClient.invalidateQueries(getShopDiscountsListingsByListingId.filters()),
  ]);
}
